import { useReducer, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Grid, Box, Divider, Typography, TextField, MenuItem, InputBase, FormControlLabel, Checkbox } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import { isValid } from 'date-fns';
import { useDispatch, useSelector } from 'store';
import { fetchAuditLogsData, fetchAuditLogsMetadata } from 'store/reducers/auditLogs';
import Loader from 'components/Loader';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import { getBEDateFormat } from 'utils/date';
import T from 'utils/constants/T';
import AuditLogsTable from './AuditLogsTable';
import { PAGINATION } from 'utils/constants/pagination';

const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    border: "1px solid #d9d9d9",
    height: '100%'
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1.5),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    height: '100%',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(3)})`,
        transition: theme.transitions.create('width'),
        width: '100%'
    },
}));

const AuditLogsPage = () => {
    const dispatch = useDispatch();
    const { logsList, totalItems, totalPages, loader, pageNo, pageSize, metadata } = useSelector((state: any) => state.auditLogsData);

    const { selectedCompany } = useSelector((state: any) => state.dashboardSearch);
    const { role } = useSelector((state: any) => state.auth) || {};
    const selectedCompanyId = selectedCompany?._id || "";

    const today = dayjs().format('YYYY-MM-DD');
    const thirtyDaysAgo = dayjs().subtract(30, 'day').format('YYYY-MM-DD');

    const [localState, setLocalState] = useReducer(
        (prevState: any, newState: any) => ({ ...prevState, ...newState }),
        {
            page: pageNo,
            rowsPerPage: pageSize,
            fromDate: thirtyDaysAgo,
            toDate: today,
            selectedModule: "",
            selectedAction: "",
            search: "",
            status: "",
            selectedRoleParam: "",
            myLogs: false,
        }
    );

    const {
        page,
        rowsPerPage,
        fromDate,
        toDate,
        selectedModule,
        selectedAction,
        search,
        status,
        selectedRoleParam,
        myLogs
    } = localState;

    useEffect(() => {
        dispatch(fetchAuditLogsMetadata());
    }, [dispatch]);

    const handlePageChange = (newPage: number) => {
        setLocalState({ page: newPage });
    };

    const handleRowsPerPageChange = (event: any) => {
        const { value } = event.target;
        setLocalState({ page: INITIAL_PAGE, rowsPerPage: value });
    };

    const onHandleDateChange = (newValue: any, type: string) => {
        const validDate = newValue ? new Date(newValue) : null;
        setLocalState({
            [type]: validDate && isValid(validDate) ? getBEDateFormat(validDate) : null,
            page: INITIAL_PAGE
        });
    };

    const handleClearFilters = () => {
        setLocalState({
            fromDate: null,
            toDate: null,
            selectedModule: "",
            selectedAction: "",
            search: "",
            status: "",
            selectedRoleParam: "",
            myLogs: false,
            page: INITIAL_PAGE,
            rowsPerPage: ROWS_PER_PAGE
        });
    };

    const onHandleChange = (e: any) => {
        const { name, value } = e.target;
        setLocalState({ [name]: value, page: INITIAL_PAGE });
    };

    useEffect(() => {
        const hasPartialDate = (fromDate && !toDate) || (!fromDate && toDate);
        if (hasPartialDate) return;

        const from = fromDate || "";
        const to = toDate || "";

        dispatch(
            fetchAuditLogsData(
                page,
                rowsPerPage,
                from,
                to,
                selectedModule,
                selectedAction,
                search,
                status,
                selectedCompanyId,
                selectedRoleParam,
                myLogs
            )
        );
    }, [
        dispatch,
        page,
        rowsPerPage,
        fromDate,
        toDate,
        selectedModule,
        selectedAction,
        search,
        status,
        selectedCompanyId,
        selectedRoleParam,
        myLogs
    ]);

    const modules = metadata ? Object.keys(metadata) : [];
    const actions = (selectedModule && metadata && metadata[selectedModule]) ? metadata[selectedModule] : [];

    if (role !== 'superadmin') {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <>
            {loader && <Loader />}
            <MainCard
                title={
                    <>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        inputFormat="DD/MM/YYYY"
                                        value={fromDate}
                                        onChange={(date) => onHandleDateChange(date, "fromDate")}
                                        renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        inputFormat="DD/MM/YYYY"
                                        value={toDate}
                                        onChange={(date) => onHandleDateChange(date, "toDate")}
                                        renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Module"
                                    name="selectedModule"
                                    value={selectedModule}
                                    onChange={onHandleChange}
                                    variant="outlined"
                                    size="small"
                                >
                                    <MenuItem value="">All Modules</MenuItem>
                                    {modules.map((mod: string) => (
                                        <MenuItem key={mod} value={mod}>{mod}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Action"
                                    name="selectedAction"
                                    value={selectedAction}
                                    onChange={onHandleChange}
                                    variant="outlined"
                                    size="small"
                                    disabled={!selectedModule}
                                >
                                    <MenuItem value="">All Actions</MenuItem>
                                    {actions.map((act: string) => (
                                        <MenuItem key={act} value={act}>{act}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    name="status"
                                    value={status}
                                    onChange={onHandleChange}
                                    variant="outlined"
                                    size="small"
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    <MenuItem value="SUCCESS">Success</MenuItem>
                                    <MenuItem value="FAILED">Failed</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Role"
                                    name="selectedRoleParam"
                                    value={selectedRoleParam}
                                    onChange={onHandleChange}
                                    variant="outlined"
                                    size="small"
                                >
                                    <MenuItem value="">All Roles</MenuItem>
                                    <MenuItem value="employee">Employee</MenuItem>
                                    <MenuItem value="employer">Employer</MenuItem>
                                    <MenuItem value="manager">Manager</MenuItem>
                                    <MenuItem value="payroll_officer">Payroll Officer</MenuItem>
                                    <MenuItem value="superadmin">Super Admin</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <Search>
                                    <SearchIconWrapper>
                                        <SearchIcon />
                                    </SearchIconWrapper>
                                    <StyledInputBase
                                        placeholder="Search..."
                                        name="search"
                                        value={search}
                                        inputProps={{ "aria-label": "search" }}
                                        onChange={onHandleChange}
                                    />
                                </Search>
                            </Grid>

                            <Grid item xs={12} sm={6} md={1.5}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={myLogs}
                                            onChange={(e) => setLocalState({ myLogs: e.target.checked, page: INITIAL_PAGE })}
                                            name="myLogs"
                                            color="primary"
                                        />
                                    }
                                    label="My Logs"
                                />
                            </Grid>

                            <Grid item xs={3} sm={3} md={1}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '100%',
                                        "& h6": {
                                            m: 1,
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            fontWeight: 600
                                        },
                                        "& hr": {
                                            mx: 0.2,
                                            my: 0.1,
                                        },
                                    }}
                                >
                                    <Divider orientation="vertical" variant="middle" flexItem />
                                    <Typography variant="h6" onClick={handleClearFilters}>
                                        {T.RESET}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </>
                }
                content={false}
            >
                <ScrollX>
                    <AuditLogsTable
                        allTableRows={logsList}
                        totalTableRowsCount={totalItems}
                        totalPageCount={totalPages}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </ScrollX>
            </MainCard>
        </>
    );
};

export default AuditLogsPage;
